import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from '../nodes'
import type { AutocompleteItem, AutocompletePluginOptions } from '../types'

const isAutocompletePlugin = <E extends AutocompleteInputElement>(
  plugin: unknown,
): plugin is { options: AutocompletePluginOptions<E> } => {
  if (
    typeof plugin !== 'object' ||
    plugin === null ||
    !('options' in plugin) ||
    typeof plugin.options !== 'object' ||
    plugin.options === null
  ) {
    return false
  }

  return (
    !('onSelectItem' in plugin.options) ||
    plugin.options.onSelectItem === undefined ||
    typeof plugin.options.onSelectItem === 'function'
  )
}

/**
 * Inserts a resolved mention at the editor's current selection. Called from
 * `InlineCombobox.Item`'s `onSelect` — by the time this runs, the trigger
 * node has already been removed (`InlineCombobox`'s own `select()` removes
 * it before invoking the callback), so this always inserts fresh rather
 * than looking the trigger node back up.
 */
export const selectAndInsertSuggestion =
  <E extends AutocompleteInputElement>(
    elementInterface: AutocompleteInputElementInterface<E>,
  ) =>
  (editor: BufferEditor, item: AutocompleteItem): void => {
    if (!editor.selection) return

    const plugin = editor.meta.pluginList.find(
      (candidate) => candidate.key === elementInterface.type,
    )

    if (!isAutocompletePlugin<E>(plugin)) return

    const { insertSpaceAfterMention = true, onSelectItem } = plugin.options

    onSelectItem?.(item)

    editor.tf.withNewBatch(() => {
      editor.tf.insertNodes(elementInterface.new(item.data))

      // Selection lands inside the void node's own (empty) children right
      // after insertion, not after the node itself — move past it first.
      editor.tf.move({ distance: 1, unit: 'offset' })

      // Make sure the next character isn't already a whitespace character
      // before inserting another
      const pointAfterSelection = editor.selection
        ? editor.api.after(editor.selection)
        : undefined
      const rangeAfterSelection =
        editor.selection && pointAfterSelection
          ? editor.api.range(editor.selection, pointAfterSelection)
          : undefined
      const nextChar = editor.api.string(rangeAfterSelection)

      if (nextChar !== ' ' && insertSpaceAfterMention) {
        editor.tf.insertText(' ')
      }
    })
  }
